import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Search, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { Account, Contact } from "@shared/schema";

interface DealParty {
  id: string;
  dealId: string;
  partyId: string;
  partyType: "account" | "contact";
  role: string;
  status: "active" | "pending";
  source?: string;
  partyName?: string; // Party display name (from API join)
}

interface AddDealPartyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  clientGroupId?: string;
  editingParty?: DealParty | null;
  onPartyAdded?: () => void; // Callback to trigger parent refresh
}

const DEAL_PARTY_ROLES = [
  { value: "borrower", label: "Borrower" },
  { value: "guarantor", label: "Guarantor" },
  { value: "authorised_signatory", label: "Authorised Signatory" },
];

export function AddDealPartyModal({
  open,
  onOpenChange,
  dealId,
  clientGroupId,
  editingParty,
  onPartyAdded,
}: AddDealPartyModalProps) {
  const isEditMode = !!editingParty;
  const [partyType, setPartyType] = useState<"account" | "contact">("account");
  const [selectedPartyId, setSelectedPartyId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("borrower");
  const [selectedStatus, setSelectedStatus] = useState<"active" | "pending">("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [editPartyName, setEditPartyName] = useState<string>("");

  // Pre-populate form when editingParty changes (edit mode)
  useEffect(() => {
    if (editingParty && open) {
      setPartyType(editingParty.partyType);
      setSelectedPartyId(editingParty.partyId);
      setSelectedRole(editingParty.role);
      setSelectedStatus(editingParty.status);
      setEditPartyName(editingParty.partyName || "Unknown Party");
    } else if (!open) {
      // Reset when modal closes
      setPartyType("account");
      setSelectedPartyId("");
      setSelectedRole("borrower");
      setSelectedStatus("active");
      setSearchTerm("");
      setEditPartyName("");
      setShowQuickAdd(false);
      setQuickAddData({ firstName: "", lastName: "", email: "", legalName: "", abn: "" });
      setAbnError("");
    }
  }, [editingParty, open]);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddData, setQuickAddData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    legalName: "",
    abn: "",
  });
  const [abnError, setAbnError] = useState<string>("");
  const { toast } = useToast();

  // Fetch accounts (filtered by client group)
  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", { ...(clientGroupId ? { clientGroupId } : {}) }],
    enabled: open && partyType === "account",
  });

  // Fetch contacts (filtered by client group)
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { ...(clientGroupId ? { clientGroupId } : {}) }],
    enabled: open && partyType === "contact",
  });

  // Fetch existing deal parties for validation
  const { data: existingPartiesData } = useQuery<{
    manual: DealParty[];
    facility_propagated: DealParty[];
    association_propagated: DealParty[];
    all: DealParty[];
  }>({
    queryKey: [`/api/crm/deals/${dealId}/parties`],
    enabled: open && !!dealId,
  });

  // Extract all existing parties (across all sources)
  const existingParties = Array.isArray(existingPartiesData?.all) ? existingPartiesData.all : [];

  const quickAddMutation = useMutation({
    mutationFn: async () => {
      if (partyType === "contact") {
        const response = await apiRequest("POST", "/api/crm/contacts", {
          clientGroupId,
          firstName: quickAddData.firstName,
          lastName: quickAddData.lastName,
          email: quickAddData.email,
          contactType: "individual",
        });
        return response.json() as Promise<Contact>;
      } else {
        const response = await apiRequest("POST", "/api/crm/accounts", {
          clientGroupId,
          legalName: quickAddData.legalName,
          abn: quickAddData.abn,
          entityType: "company",
        });
        return response.json() as Promise<Account>;
      }
    },
    onSuccess: async (newEntity: Contact | Account) => {
      // Optimistic cache update - synchronously update cache AND trigger re-render
      if (partyType === 'account') {
        queryClient.setQueryData(
          ["/api/crm/accounts", clientGroupId],
          (old: Account[] | undefined) => [...(old || []), newEntity as Account]
        );
      } else {
        queryClient.setQueryData(
          ["/api/crm/contacts", clientGroupId],
          (old: Contact[] | undefined) => [...(old || []), newEntity as Contact]
        );
      }

      // Auto-select AFTER cache is updated (ISSUE-129 fix iteration 3)
      setSelectedPartyId(newEntity.id);

      // Invalidate for eventual consistency (background refetch)
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", clientGroupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts", clientGroupId] });

      toast({
        title: `${partyType === "contact" ? "Contact" : "Account"} created`,
        description: `The ${partyType} has been successfully created and selected.`,
      });

      setShowQuickAdd(false);
      setQuickAddData({ firstName: "", lastName: "", email: "", legalName: "", abn: "" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || `Failed to create ${partyType}`,
        variant: "destructive",
      });
    },
  });

  const addPartyMutation = useMutation({
    mutationFn: async () => {
      if (isEditMode && editingParty) {
        // PATCH for update
        await apiRequest("PATCH", `/api/crm/deals/${dealId}/parties/${editingParty.id}`, {
          role: selectedRole,
          status: selectedStatus,
        });
      } else {
        // POST for create
        await apiRequest("POST", `/api/crm/deals/${dealId}/parties`, {
          partyId: selectedPartyId,
          partyType,
          role: selectedRole,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/parties`] });
      toast({
        title: isEditMode ? "Party updated" : "Party added",
        description: isEditMode
          ? "The party has been successfully updated."
          : "The party has been successfully added to this deal.",
      });

      // Trigger parent refresh callback
      if (onPartyAdded) {
        onPartyAdded();
      }

      handleClose();
    },
    onError: (error: Error) => {
      // apiRequest supplies bounded plain text with a status prefix.
      const errorMessage = error.message.replace(/^\d+:\s*/, "") ||
        (isEditMode ? "Failed to update party" : "Failed to add party");
      const errorTitle = error.message.startsWith("409:") ? "Duplicate party" :
        error.message.startsWith("404:") ? "Not found" : "Error";

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    // Reset logic moved to useEffect (triggered when open becomes false)
  };

  const validateABN = (abn: string): string => {
    // Remove spaces and validate length
    const cleanABN = abn.replace(/\s/g, "");
    if (cleanABN.length === 0) return ""; // Allow empty (will be caught by required validation)
    if (cleanABN.length > 11) return "ABN cannot exceed 11 digits";
    if (cleanABN.length > 0 && cleanABN.length < 11) return "ABN must be exactly 11 digits";
    if (!/^\d+$/.test(cleanABN)) return "ABN must contain only digits";
    return ""; // Valid
  };

  const handleABNChange = (value: string) => {
    setQuickAddData({ ...quickAddData, abn: value });
    const error = validateABN(value);
    setAbnError(error);
  };

  // Validate role conflicts (ISSUE-139)
  const validateRoleConflict = (): boolean => {
    // In edit mode, allow same role for same party (no conflict with self)
    const partiesToCheck = isEditMode && editingParty
      ? existingParties.filter(p => p.id !== editingParty.id)
      : existingParties;

    // Find if this party already exists with a conflicting role
    const conflictingParty = partiesToCheck.find(
      (party) =>
        party.partyId === selectedPartyId &&
        ((selectedRole === "borrower" && party.role === "guarantor") ||
          (selectedRole === "guarantor" && party.role === "borrower"))
    );

    if (conflictingParty) {
      const existingRoleLabel = conflictingParty.role === "borrower" ? "Borrower" : "Guarantor";

      toast({
        title: "Role conflict",
        description: `This party is already a ${existingRoleLabel} on this deal. A party cannot be both Borrower and Guarantor.`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleAdd = () => {
    if (!selectedPartyId) {
      toast({
        title: "No party selected",
        description: "Please select a party to add to this deal.",
        variant: "destructive",
      });
      return;
    }

    // Validate role conflict before submitting
    if (!validateRoleConflict()) {
      return;
    }

    addPartyMutation.mutate();
  };

  const handleQuickAdd = () => {
    if (partyType === "contact") {
      if (!quickAddData.firstName || !quickAddData.lastName || !quickAddData.email) {
        toast({
          title: "Missing fields",
          description: "Please fill in first name, last name, and email.",
          variant: "destructive",
        });
        return;
      }
    } else {
      if (!quickAddData.legalName || !quickAddData.abn) {
        toast({
          title: "Missing fields",
          description: "Please fill in legal name and ABN.",
          variant: "destructive",
        });
        return;
      }
    }
    quickAddMutation.mutate();
  };

  // Filter parties by search term (with null-safety)
  const filteredAccounts = accounts.filter((account) =>
    account.legalName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false
  );

  const filteredContacts = contacts.filter((contact) => {
    const fullName = `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
    return fullName ? fullName.toLowerCase().includes(searchTerm.toLowerCase()) : false;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit Deal Party" : "Add Party to Deal"}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? `Editing: ${editPartyName} - Update the role or status for this party`
              : "Add a manual party to this deal (not linked to facilities)"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Only show party selection when creating new party */}
          {!isEditMode && (
            <>
              {/* Party Type Selection */}
              <div className="space-y-2">
                <Label>Party Type</Label>
                <RadioGroup value={partyType} onValueChange={(value: any) => {
                  setPartyType(value);
                  setSelectedPartyId("");
                }}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="account" id="account" />
                    <Label htmlFor="account" className="font-normal cursor-pointer">Account/Company</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="contact" id="contact" />
                    <Label htmlFor="contact" className="font-normal cursor-pointer">Contact/Individual</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Search */}
              <div className="space-y-2">
                <Label>Search {partyType === "account" ? "Accounts" : "Contacts"}</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={`Search ${partyType === "account" ? "accounts" : "contacts"}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>

              {/* Party Selection */}
              <div className="space-y-2">
                <Label>Select {partyType === "account" ? "Account" : "Contact"}</Label>
                <Select value={selectedPartyId} onValueChange={setSelectedPartyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={`Select ${partyType}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {partyType === "account"
                      ? filteredAccounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.legalName}
                          </SelectItem>
                        ))
                      : filteredContacts.map((contact) => (
                          <SelectItem key={contact.id} value={contact.id}>
                            {contact.firstName} {contact.lastName}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Quick Add Buttons - Only show when not editing */}
          {!isEditMode && (
            <>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPartyType("contact");
                    setShowQuickAdd(!showQuickAdd);
                  }}
                  className="flex-1"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add New Contact
                  {showQuickAdd && partyType === "contact" ? (
                    <ChevronUp className="ml-2 h-4 w-4" />
                  ) : (
                    <ChevronDown className="ml-2 h-4 w-4" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPartyType("account");
                    setShowQuickAdd(!showQuickAdd);
                  }}
                  className="flex-1"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add New Account
                  {showQuickAdd && partyType === "account" ? (
                    <ChevronUp className="ml-2 h-4 w-4" />
                  ) : (
                    <ChevronDown className="ml-2 h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Inline Quick Add Form */}
              {showQuickAdd && (
            <div className="rounded-md border border-border bg-muted/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">
                  Create New {partyType === "contact" ? "Contact" : "Account"}
                </h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowQuickAdd(false)}
                >
                  Cancel
                </Button>
              </div>
              {partyType === "contact" ? (
                <>
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <Input
                      placeholder="Enter first name"
                      value={quickAddData.firstName}
                      onChange={(e) =>
                        setQuickAddData({ ...quickAddData, firstName: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input
                      placeholder="Enter last name"
                      value={quickAddData.lastName}
                      onChange={(e) =>
                        setQuickAddData({ ...quickAddData, lastName: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="Enter email"
                      value={quickAddData.email}
                      onChange={(e) =>
                        setQuickAddData({ ...quickAddData, email: e.target.value })
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Legal Name</Label>
                    <Input
                      placeholder="Enter company legal name"
                      value={quickAddData.legalName}
                      onChange={(e) =>
                        setQuickAddData({ ...quickAddData, legalName: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ABN</Label>
                    <Input
                      placeholder="Enter ABN (11 digits)"
                      value={quickAddData.abn}
                      onChange={(e) => handleABNChange(e.target.value)}
                      className={abnError ? "border-destructive" : ""}
                    />
                    {abnError && (
                      <p className="text-xs text-destructive">{abnError}</p>
                    )}
                  </div>
                </>
              )}
              <p className="text-xs text-muted-foreground">
                Client group inherited from deal context (locked)
              </p>
              <Button
                type="button"
                onClick={handleQuickAdd}
                disabled={quickAddMutation.isPending || (partyType === "account" && !!abnError)}
                className="w-full"
              >
                {quickAddMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create & Link
              </Button>
            </div>
              )}
            </>
          )}

          {/* Role Selection */}
          <div className="space-y-2">
            <Label>Party Role</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger data-testid="select-party-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEAL_PARTY_ROLES.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Selection - Only show when editing */}
          {isEditMode && (
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={(value) => setSelectedStatus(value as "active" | "pending")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={addPartyMutation.isPending || (!isEditMode && !selectedPartyId)}
          >
            {addPartyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditMode ? "Update Party" : "Add Party"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
